import {Notice} from './partial/notice'
import {Description} from './partial/product/description'
import {Status} from './partial/product/status'
import {SubStatus} from './partial/product/subStatus'

export const ProductType = {
    /** 일반 상품 */
    NORMAL: 'NORMAL',
    /** 렌탈 상품 */
    RENTAL: 'RENTAL',
    /** 정기 구독 상품 */
    SUBSCRIPTION: 'SUBSCRIPTION',
    /** 맞춤제작 상품 */
    CUSTOMIZED: 'CUSTOMIZED',
    /** 선물하기 상품 */
    GIFT: 'GIFT',
    /** 판매하는 상품 */
    SELLING: 'SELLING',
    /** 해외결제 상품 */
    CROSSBORDER: 'CROSSBORDER',
} as const
export type ProductTypeType = Constant<typeof ProductType>

/** 상품 정보 */
export type Product =
    /** 일반 상품 */
    | NormalProduct
    /** 렌탈 상품 */
    | RentalProduct
    /** 정기 구독 */
    | SubscriptionProduct
    /** 맞춤제작 상품 */
    | CustomizedProduct
    /** 선물하기 상품 */
    | GiftProduct
    /** 판매하는 상품 */
    | SellingProduct
    /** 해외결제 상품 */
    | CrossborderProduct

type BaseProduct<Type extends ProductTypeType, Extra = undefined> = {
    /** 번들키 */
    bundleKey: string
    /** 서브 번들키 */
    subBundleKey: string
    /** 타입 */
    type: Type
    /** 상태 */
    status: Status
    /** 서브 상태 */
    subStatus?: SubStatus
    /** 아이디 */
    id: string
    /** 아이디 타입 */
    idType?: Constant<typeof IdType>
    /** 상품명 */
    name: {
        text: string
        /** 상품명 클릭시 이동될 URL */
        href?: string
    }
    /** 가격 */
    price?: {
        /** 원가 */
        original: number
        /** 할인된 가격 */
        discounted?: number
    }
    /** 구매 갯수 */
    quantity?: number
    /** 상품 옵션 */
    option?: {
        /** 옵션 이름 */
        name: string
        /** 추가 요금 */
        surcharge?: number
        /** 갯수 */
        quantity?: number
    }
    /** 상품 설명 */
    description?: Description
    /** 사은품 */
    freeGift?: string
    /** 상품 이미지 */
    image?: {
        /** 상품 이미지 리소스 */
        src?: string
        /** 상품 이미지 링크 */
        href?: string
    }
    /** 상품 상세 URL */
    detailUrl?: string
    /** 네이버 멤버십 */
    naverMembership: boolean
    /** 취소/환불/반품 정보 */
    claim?: {
        type: 'CANCEL' | 'RETURN' | 'REFUND'
        amount: number
        reason?: string
    }
    /** 안내 */
    notice?: Notice
    /** 결제 날짜 */
    paymentDate?: string
    /** 과업관련 */
    assignment?: {
        /** 취소과업 > 배송방법에 따른 분기 */
        deliveryMethod?: 'CANCEL_REJECT' | 'DELIVERY_DESIGNATED' | 'CONVENIENCE_DELIVERY'
    }
} & ConditionalField<Extra, 'extra'>

export type NormalProduct = BaseProduct<typeof ProductType.NORMAL>

export type RentalProduct = BaseProduct<
    typeof ProductType.RENTAL,
    {
        /** 회차 */
        cycle: number
        /** 관리 */
        management: {
            method: '방문관리' | '자체관리'
            cycle: number
        }
        /** 선납금 */
        prepayment: {
            amount: number
        }
    }
>

export type SubscriptionProduct = BaseProduct<
    typeof ProductType.SUBSCRIPTION,
    {
        /** 회차 */
        cycle: number
    }
>

export type CustomizedProduct = BaseProduct<
    typeof ProductType.CUSTOMIZED,
    {
        /** 주문 후 제작인지 여부 */
        madeToOrder: boolean
    }
>

export type GiftProduct = BaseProduct<
    typeof ProductType.GIFT,
    {
        type: /** 실물 */
            | 'PHYSICAL'
            /** 디지털  */
            | 'DIGITAL'
        receiver: {
            name: string
            id: string
        }
    }
>

export type SellingProduct = BaseProduct<
    typeof ProductType.SELLING,
    {
        receiver: {
            /** 거래일 */
            transactionDate: string
            /** 날짜 타입 */
            dateType: Constant<typeof ReceiverTransactionDateType>
            /** 구매자 이름 */
            name?: string
            /** (구매자 별칭) */
            subName?: string
            /** 구매자 아이디 */
            id?: string
        }
        title?: {
            noticeType?: 'TRANSACTION_CONFIRMED'
        }
    }
>

export type CrossborderProduct = BaseProduct<
    typeof ProductType.CROSSBORDER,
    {
        /** 결제 파트너사 */
        partner: {
            name: string
        }
        /** 현지 통화 정보 */
        localCurrency?: {
            /** 통화 코드 (예: USD, JPY) */
            code: string
            /** 현지 통화 금액 */
            amount: string
            /** 프로모션 할인 적용 여부 (금액이 0원 초과인 경우 true) */
            hasPromotionDiscount: boolean
        }
    }
>

export const ReceiverTransactionDateType = {
    /** 주문일 */
    ORDER: 'ORDER',
    /** 결제일 */
    PAYMENT: 'PAYMENT',
} as const

export const IdType = {
    /** 승인번호 */
    APPROVAL_NUM: 'APPROVAL_NUM',
    /** 결제확인번호 */
    PAYMENT_CONFIRMATION_NUM: 'PAYMENT_CONFIRMATION_NUM',
} as const

export * from './partial/product'
